import { config } from './config.js';

/**
 * The admin console's single seam to the platform.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * EVERY PATH HERE STARTS `/api/admin`, AND THAT IS ENFORCED
 * ══════════════════════════════════════════════════════════════════════════
 *
 * This console holds a token that no tenant endpoint would accept anyway — the
 * platform's two verifiers pin different audiences — so a call to `/api/ledger`
 * would fail. It would fail as a confusing 401 rather than as a mistake
 * somebody can see, and in production the two are different origins, so it
 * would fail differently again.
 *
 * `request` therefore refuses a path outside the prefix before it reaches the
 * network. One place to look, one rule, and a stray call is a thrown error
 * naming the problem rather than an authentication puzzle.
 */

const PREFIX = '/api/admin';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Raised when the platform says the session is gone. The shell signs out. */
export class SessionExpired extends ApiError {
  constructor() {
    super(401, 'Your admin session has ended.');
    this.name = 'SessionExpired';
  }
}

/**
 * What the platform says about signing in here. Nothing in it is a secret.
 *
 * Two shapes, exactly as the product console's `/api/auth/config` has two:
 * `dev` means "pick an identity", `oidc` means "go and sign in". Local and
 * deployed then run the same code down the same path, which is the whole
 * reason this is a runtime answer rather than a build-time flag.
 */
export type AdminSignInConfig =
  | { mode: 'dev' }
  | {
      mode: 'oidc';
      issuer: string;
      /** The ADMIN app client. Never the product console's. */
      clientId: string;
      scopes: string[];
    };

/** A roster identity a dev sign-in may pick. Local mode only. */
export interface DevAdmin {
  email: string;
  name: string | null;
  role: string;
}

/** An account, as the admin console is allowed to see it. Metadata only. */
export interface TenantSummary {
  id: string;
  slug: string;
  name: string;
  planCode: string | null;
  status: string;
  entitlements: string[];
  demoResettable: boolean;
  createdAt: string;
  members: number;
  events: number;
  lastEventAt: string | null;
}

/** What is on an account, by group. The confirmation before a purge. */
export interface DataSummary {
  slug: string | null;
  name: string | null;
  events: number;
  totalRows: number;
  groups: Array<{ group: string; tables: number; rows: number }>;
  /** Named, not counted: an unclassified table is a task for whoever added it. */
  unclassified: string[];
}

/** Whether an account is a demo fixture, and what a reset would cost. */
export interface DemoResetStatus {
  resettable: boolean;
  name: string | null;
  events: number;
}

export interface PurgeResult {
  slug: string;
  auditId: string;
  /** Read from the admin session, never from what the console sent. */
  actor: string;
  cleared: Array<{ table: string; rows: number }>;
  totalRows: number;
}

export interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  role: string;
  status: string;
  isBootstrap: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  lastLoginIp: string | null;
}

export interface AuditEntry {
  id: string;
  createdAt: string;
  actor: string;
  action: string;
  subjectTenantId: string | null;
  subjectSlug: string | null;
  status: string;
  metadata: Record<string, unknown>;
}

export interface AuditPage {
  entries: AuditEntry[];
  /** Matching the filter, not the page. */
  total: number;
}

export interface AdminMe {
  email: string;
  name: string | null;
  role: string;
  isBootstrap: boolean;
  expiresAt: string;
  sessionId: string;
}


/* ───────────────────────── feature flags ───────────────────────── */

// The order lives with the rules that read it, in features/flags/stage-order.ts,
// so those rules stay testable without a browser build.
import { FLAG_STAGES, type FlagStage } from '../features/flags/stage-order.js';
export { FLAG_STAGES, type FlagStage };

/** Fills in a name for a flag served by a platform that predates labels. */
function named(flag: FlagState): FlagState {
  return {
    ...flag,
    label: flag.label || flag.key,
    description: flag.description || '',
  };
}

export interface FlagState {
  key: string;
  /** What to call it on screen. The key is the subtitle, never the headline. */
  label: string;
  description: string;
  stage: FlagStage;
  /** Cosmos staff who see it at `internal`. Admin user ids. */
  internalUserIds: string[];
  alphaTenantIds: string[];
  betaTenantIds: string[];
  /** Per-account answers that beat the stage, either way. */
  overrides: Record<string, boolean>;
  updatedAt: string;
}

export interface FlagsResponse {
  /** Which backend answers. `builtin` keeps them in this deployment. */
  provider: string;
  /**
   * False when a hosted backend owns its own console.
   *
   * The screen then says where flags are managed instead of rendering controls
   * that write nowhere.
   */
  managedHere: boolean;
  flags: FlagState[];
}

export interface FlagPatch {
  stage?: FlagStage;
  internalUserIds?: string[];
  alphaTenantIds?: string[];
  betaTenantIds?: string[];
  /** `null` removes an override; `false` keeps an account OUT of a release. */
  overrides?: Record<string, boolean | null>;
}

export class AdminApi {
  constructor(private readonly token: string | null) {}

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    if (!path.startsWith(PREFIX)) {
      throw new Error(
        `Refusing to call "${path}": the admin console may only reach ${PREFIX}. ` +
          'A tenant endpoint would refuse this token anyway, and would do it as a ' +
          'confusing 401 rather than as a visible mistake.',
      );
    }

    const res = await fetch(`${config.apiBaseUrl}${path}`, {
      ...init,
      headers: {
        'content-type': 'application/json',
        ...(this.token ? { authorization: `Bearer ${this.token}` } : {}),
        ...init.headers,
      },
    });

    if (res.status === 401) {
      /*
        The platform answers 401 both for a token it cannot verify and for a
        session that has been closed, expired or suspended — deliberately
        indistinguishable from outside. The console treats them the same way
        too: sign out and start again. Trying to tell them apart here would be
        inventing a distinction the platform refused to make.
      */
      throw new SessionExpired();
    }

    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      throw new ApiError(res.status, body.error ?? `Request failed (${res.status})`);
    }
    return (await res.json()) as T;
  }

  /**
   * Where to sign in, asked of the platform rather than built into this bundle.
   *
   * ══════════════════════════════════════════════════════════════════════════
   * WHY THIS IS NOT A BUILD-TIME ENVIRONMENT VARIABLE
   * ══════════════════════════════════════════════════════════════════════════
   *
   * The client id this console must use is the ADMIN app client's, and the one
   * thing it must never be is the product console's. Two `VITE_` variables in
   * two repositories, set by hand, is precisely how two values that must differ
   * come to be the same — and nothing would report it. Both consoles would sign
   * in, and the audience split that keeps a product token from becoming an
   * admin session would silently be gone.
   *
   * Asking the platform means the console cannot hold an opinion different from
   * the verifier that will judge its token. There is one value, in one place.
   *
   * ── NULL MEANS "NO ADMIN SURFACE HERE", AND IT ARRIVES AS 401 OR 404 ──────
   *
   * The platform mounts `/api/admin` only when an admin audience is configured.
   * Unmounted, the obvious answer is 404 — and it is not what happens. The
   * request falls through to the platform's authenticated `/api` router, which
   * runs its token check before discovering there is no such route, so a laptop
   * in dev mode answers **401**.
   *
   * This was written to treat 404 alone as "not configured", and the 401 became
   * a thrown error that surfaced as "Nothing answered" — a plumbing failure
   * reported where the truthful message is "this platform has no admin surface".
   * Found by curling the running container rather than by reasoning about it.
   *
   * Both are unambiguous: a MOUNTED admin config route is unauthenticated and
   * cannot answer 401, so either status means the same thing.
   */
  static async signInConfig(): Promise<AdminSignInConfig | null> {
    const res = await fetch(`${config.apiBaseUrl}${PREFIX}/auth/config`);
    if (res.status === 404 || res.status === 401) return null;
    if (!res.ok) throw new ApiError(res.status, `Sign-in configuration unavailable (${res.status})`);
    return (await res.json()) as AdminSignInConfig;
  }

  /**
   * Trade a verified provider token for an admin session token.
   *
   * The only call made WITHOUT an admin token, because it is how one is
   * obtained. The platform verifies the provider token, checks the roster, and
   * answers 403 to a verified identity that is not an administrator.
   */
  static async exchange(idToken: string): Promise<{ token: string; expiresIn: number }> {
    const res = await fetch(`${config.apiBaseUrl}${PREFIX}/auth/exchange`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ idToken }),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      throw new ApiError(res.status, body.error ?? 'Sign-in failed.');
    }
    return (await res.json()) as { token: string; expiresIn: number };
  }

  /**
   * The identities a LOCAL dev sign-in may pick from.
   *
   * Only the bootstrap rows, not the roster: the endpoint behind this is
   * unauthenticated (it is how a laptop gets its first session), and an
   * unauthenticated endpoint that enumerates administrators is a different
   * thing from one that mints a session for a known account.
   */
  static async devAdmins(): Promise<DevAdmin[]> {
    const res = await fetch(`${config.apiBaseUrl}${PREFIX}/auth/dev-admins`);
    if (!res.ok) throw new ApiError(res.status, 'The dev sign-in list is unavailable.');
    return (await res.json()) as DevAdmin[];
  }

  /**
   * Assert an identity, locally.
   *
   * The ROSTER still decides: the platform opens the session through the same
   * function production uses, which refuses any address that is not an active
   * administrator. So a 403 here means exactly what it means deployed, and the
   * authorisation model is the thing being developed rather than stubbed.
   */
  static async devToken(email: string): Promise<{ token: string; expiresIn: number }> {
    const res = await fetch(`${config.apiBaseUrl}${PREFIX}/auth/dev-token`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      throw new ApiError(res.status, body.error ?? 'Sign-in failed.');
    }
    return (await res.json()) as { token: string; expiresIn: number };
  }

  /** Who the platform says this session belongs to, re-read on every call. */
  me(): Promise<AdminMe> {
    return this.request<AdminMe>(`${PREFIX}/me`);
  }

  /* ─────────────────────────── tenants ─────────────────────────── */

  tenants(): Promise<TenantSummary[]> {
    return this.request<TenantSummary[]>(`${PREFIX}/tenants`);
  }

  /**
   * Create an account and invite its owner.
   *
   * The platform refuses a slug that already exists, and that refusal is not a
   * formality: iam's provisioning is an upsert, so without the check a repeat
   * slug would rename the live account and report success.
   */
  createTenant(input: {
    slug: string;
    name: string;
    planCode: string;
    ownerEmail: string;
    ownerName?: string;
  }): Promise<{ tenantId: string; ownerId: string; slug: string }> {
    return this.request(`${PREFIX}/tenants`, { method: 'POST', body: JSON.stringify(input) });
  }

  tenant(id: string): Promise<TenantSummary> {
    return this.request<TenantSummary>(`${PREFIX}/tenants/${encodeURIComponent(id)}`);
  }

  dataSummary(id: string): Promise<DataSummary> {
    return this.request<DataSummary>(`${PREFIX}/tenants/${encodeURIComponent(id)}/data`);
  }

  /**
   * Can this account be reset, and what would it cost?
   *
   * Returns reasons rather than a boolean: a screen that can only say "not
   * available" leaves an operator guessing whether the control is missing
   * because of the account or because of them.
   */
  resetStatus(id: string): Promise<DemoResetStatus> {
    return this.request<DemoResetStatus>(`${PREFIX}/tenants/${encodeURIComponent(id)}/reset`);
  }

  /**
   * Put a demo account back to its seeded state.
   *
   * A different verb from the purge. This REBUILDS — chart of accounts, the one
   * governed action — and the platform refuses it outright on any account not
   * explicitly marked as a demo, which is every real customer. That refusal is
   * why there is no typed confirmation: the blast radius is an account that
   * exists to be emptied.
   */
  resetTenant(id: string): Promise<{ cleared: Array<{ table: string; rows: number }>; totalRows: number }> {
    return this.request(`${PREFIX}/tenants/${encodeURIComponent(id)}/reset`, { method: 'POST' });
  }

  /**
   * Destroy an account's data.
   *
   * `confirmSlug` is checked against the slug the PLATFORM holds for this
   * account, not against what this console thought it was looking at. So a
   * console showing a stale page cannot confirm its way past the check, and
   * the 409 that comes back names the account the platform actually has.
   */
  purge(id: string, confirmSlug: string): Promise<PurgeResult> {
    return this.request<PurgeResult>(`${PREFIX}/tenants/${encodeURIComponent(id)}/purge`, {
      method: 'POST',
      body: JSON.stringify({ confirmSlug }),
    });
  }


  /* ─────────────────────────── feature flags ─────────────────────────── */
  /*
    `label` and `description` arrive from the platform's flag registry. They
    are declared required above, because every screen needs a name and none
    should carry its own fallback.

    This console and the platform deploy from separate repos, so for the length
    of one rollout the API can be a version behind and send neither. Normalise
    it HERE, once, rather than letting each screen guess: a missing label would
    otherwise render an empty heading, which is worse than the key it replaced.

    Delete this when the two have been in step for a release.
  */

  async flags(): Promise<FlagsResponse> {
    const res = await this.request<FlagsResponse>(`${PREFIX}/flags`);
    return { ...res, flags: res.flags.map(named) };
  }

  async setFlag(key: string, patch: FlagPatch): Promise<FlagState> {
    return named(
      await this.request<FlagState>(`${PREFIX}/flags/${encodeURIComponent(key)}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    );
  }

  /* ─────────────────────────── the roster ─────────────────────────── */

  admins(): Promise<AdminUser[]> {
    return this.request<AdminUser[]>(`${PREFIX}/admins`);
  }

  addAdmin(input: { email: string; name?: string; role: string }): Promise<{ id: string }> {
    return this.request<{ id: string }>(`${PREFIX}/admins`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  setAdminStatus(id: string, status: string, reason?: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(`${PREFIX}/admins/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ status, reason }),
    });
  }

  /* ─────────────────────────── the trail ─────────────────────────── */

  audit(query: { limit?: number; offset?: number; tenantId?: string } = {}): Promise<AuditPage> {
    const params = new URLSearchParams();
    if (query.limit !== undefined) params.set('limit', String(query.limit));
    if (query.offset !== undefined) params.set('offset', String(query.offset));
    if (query.tenantId) params.set('tenantId', query.tenantId);
    const qs = params.toString();
    return this.request<AuditPage>(`${PREFIX}/audit${qs ? `?${qs}` : ''}`);
  }

  /**
   * Sign out, and mean it: the platform closes the session ROW, so the token
   * is worth nothing from the next request onward even though it stays
   * cryptographically valid until it expires.
   */
  logout(): Promise<{ closed: boolean }> {
    return this.request<{ closed: boolean }>(`${PREFIX}/auth/logout`, { method: 'POST' });
  }
}
